// client/src/pages/TaskDashboard.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function TaskDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [rating, setRating] = useState(5);

  useEffect(() => {
    loadTask();
  }, [id]);

  const loadTask = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/tasks/${id}`);
      const data = await res.json();

      if (!res.ok) {
        alert(data.error);
        return navigate("/profile");
      }

      const normalized = data.task || data;
      normalized.applicants = Array.isArray(normalized.applicants)
        ? normalized.applicants
        : [];

      setTask(normalized);
    } catch {
      alert("Error loading task");
      navigate("/profile");
    } finally {
      setLoading(false);
    }
  };

  const markCompleted = async () => {
    if (!window.confirm("Mark task as completed?")) return;

    setProcessing(true);

    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/tasks/${id}/mark-completed`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      setProcessing(false);

      if (!res.ok) return alert(data.error);

      alert("Marked as completed. Waiting for owner confirmation.");
      loadTask();
    } catch {
      alert("Error marking completed");
    }
  };

  const confirmCompletion = async () => {
    if (!window.confirm("Confirm completion and rate applicant?")) return;

    setProcessing(true);

    try {
      const token = localStorage.getItem("cp_token");
      const res = await fetch(`${API}/tasks/${id}/confirm-completion`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating }),
      });

      const data = await res.json();
      setProcessing(false);

      if (!res.ok) return alert(data.error);

      alert("Task completed + applicant rated!");

      // refresh user if THIS is the applicant
      if (data.updatedApplicant && data.updatedApplicant.id === user.id) {
        setUser(data.updatedApplicant);
      }

      navigate("/profile");
    } catch {
      alert("Error");
    }
  };

  const handleAccept = async (applicantId) => {
    if (!window.confirm("Accept applicant?")) return;

    setProcessing(true);

    try {
      const token = localStorage.getItem("cp_token");

      const res = await fetch(`${API}/tasks/${id}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ applicantId }),
      });

      const data = await res.json();
      setProcessing(false);

      if (!res.ok) return alert(data.error);

      alert("Applicant accepted!");
      setTask(data.task);
    } catch {
      alert("Error accepting applicant");
    }
  };

  const handleReject = async (applicantId) => {
    if (!window.confirm("Reject applicant?")) return;

    setProcessing(true);

    try {
      const token = localStorage.getItem("cp_token");

      const res = await fetch(`${API}/tasks/${id}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ applicantId }),
      });

      const data = await res.json();
      setProcessing(false);

      if (!res.ok) return alert(data.error);

      alert("Applicant rejected.");
      setTask(data.task);
    } catch {
      alert("Error rejecting applicant");
    }
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;
  if (!task) return <div className="p-6 text-center">Task not found</div>;

  const isOwner = String(task.postedBy) === String(user.id);
  const isApplicant =
    task.acceptedApplicant &&
    String(task.acceptedApplicant) === String(user.id);

  return (
    <div className="max-w-4xl mx-auto p-8">

      <button
        onClick={() => navigate("/profile")}
        className="mb-6 px-4 py-2 bg-gray-200 rounded"
      >
        ← Back
      </button>

      <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow">

        <h1 className="text-3xl font-bold">{task.title}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          {task.description}
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-300 mb-4">
          Posted by{" "}
          <span className="font-medium">
            {task.postedByName || task.postedBy || "Anonymous"}
          </span>
        </p>


        <div className="mt-4 flex gap-6">
          <span>Category: {task.category}</span>
          <span>Budget: ₹{task.budgetMin} - ₹{task.budgetMax}</span>
          <span>
            Due:{" "}
            {task.dueDate
              ? new Date(task.dueDate).toLocaleDateString()
              : "N/A"}
          </span>
        </div>

        {/* APPLICANT ACTIONS */}
        {isApplicant && task.status === "in_progress" && (
          <div className="mt-6 p-4 bg-blue-100 dark:bg-blue-900 rounded">
            <h3 className="text-xl font-semibold">You are assigned.</h3>

            <button
              onClick={markCompleted}
              disabled={processing}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded"
            >
              Mark Completed
            </button>
          </div>
        )}

        {isApplicant && task.status === "pending_completion" && (
          <p className="mt-6 text-yellow-500 font-semibold">
            Waiting for owner to confirm completion...
          </p>
        )}

        {/* OWNER CONFIRM COMPLETION */}
        {isOwner && task.status === "pending_completion" && (
          <div className="mt-6 p-4 bg-green-100 dark:bg-green-900 rounded">
            <h3 className="text-xl font-bold">Applicant marked as completed.</h3>

            <p className="mt-2">Give rating:</p>

            <select
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              className="mt-2 p-2 rounded bg-white dark:bg-slate-700"
            >
              <option value={5}>⭐️⭐️⭐️⭐️⭐️</option>
              <option value={4}>⭐️⭐️⭐️⭐️</option>
              <option value={3}>⭐️⭐️⭐️</option>
              <option value={2}>⭐️⭐️</option>
              <option value={1}>⭐️</option>
            </select>

            <button
              onClick={confirmCompletion}
              className="block mt-4 px-4 py-2 bg-green-600 text-white rounded"
            >
              Confirm Completion
            </button>
          </div>
        )}

        {/* APPLICANT LIST FOR OWNER */}
        {isOwner && task.status === "open" && task.applicants.length > 0 && (
          <div className="mt-6">
            <h3 className="text-2xl font-bold">Applicants</h3>

            {task.applicants.map((app) => (
              <div
                key={app.userId}
                className="p-4 mt-3 flex justify-between bg-gray-50 dark:bg-slate-700 rounded"
              >
                <div>
                  <p className="font-semibold">{app.name}</p>
                  <p className="text-sm">{app.email}</p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleAccept(app.userId)}
                    className="px-4 py-1 bg-green-600 text-white rounded"
                  >
                    Accept
                  </button>

                  <button
                    onClick={() => handleReject(app.userId)}
                    className="px-4 py-1 bg-red-600 text-white rounded"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
